/**
 * Supported casing styles.
 */
export type Casing
  = | 'camel' // camelCase
    | 'pascal' // PascalCase
    | 'snake' // snake_case
    | 'kebab' // kebab-case
    | 'constant' // CONSTANT_CASE
    | 'title'; // Title Case (e.g., "Title Case Style")

/**
 * Regex-safe splitting of camelCase, PascalCase, acronyms, and digit boundaries.
 * e.g., "getUser2FA" -> ["get", "User", "2", "FA"]
 */
const splitCasingDelimiters = (word: string): string[] => {
  return word
    .replace(/([a-z\d])([A-Z])/g, '$1 $2') // camel → space
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // acronym boundaries
    .replace(/([a-zA-Z])(\d)/g, '$1 $2') // letter-digit
    .replace(/(\d)([a-zA-Z])/g, '$1 $2') // digit-letter
    .split(' ')
    .filter(Boolean);
};

/**
 * Matches any common non-casing delimiters.
 */
const nonCasingDelimiters = /[\s,._-]+/;

/**
 * Returns the string delimiter used for a given casing style.
 */
const getDelimiter = (target: Casing): string => {
  switch (target) {
    case 'constant':
    case 'snake':
      return '_';
    case 'kebab':
      return '-';
    case 'title':
      return ' ';
    default: // camel, pascal
      return '';
  }
};

/**
 * Capitalizes the first letter of a string.
 */
const capitaliseFirstLetter = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/**
 * Applies the transformation rules for the target casing format.
 */
const convertToTargetCasing = (
  word: string,
  index: number,
  target: Casing,
): string => {
  switch (target) {
    case 'constant':
      return word.toUpperCase();
    case 'snake':
    case 'kebab':
      return word.toLowerCase();
    case 'pascal':
      return capitaliseFirstLetter(word);
    case 'camel':
      return index === 0
        ? word.toLowerCase()
        : capitaliseFirstLetter(word);
    case 'title':
      return capitaliseFirstLetter(word);
    default:
      throw new Error(`Unsupported casing: ${target}`);
  }
};

/**
 * Converts a string to a specified casing style.
 *
 * @param text - The input string.
 * @param target - The target casing format.
 * @returns The converted string.
 */
export const convertCase = (text: string, target: Casing): string => text
  .split(nonCasingDelimiters)
  .flatMap(splitCasingDelimiters)
  .map((word, index) => convertToTargetCasing(word, index, target))
  .join(getDelimiter(target));
;

/**
 * Converts a string to camelCase.
 *
 * @example
 * camelCase("get_user-id2FA") // "getUserId2Fa"
 */
export const camelCase = (text: string) => convertCase(text, 'camel');

/**
 * Converts a string to PascalCase.
 *
 * @example
 * pascalCase("get_user-id2FA") // "GetUserId2Fa"
 */
export const pascalCase = (text: string) => convertCase(text, 'pascal');

/**
 * Converts a string to snake_case.
 *
 * @example
 * snakeCase("getUserId2FA") // "get_user_id_2_fa"
 */
export const snakeCase = (text: string) => convertCase(text, 'snake');

/**
 * Converts a string to kebab-case.
 *
 * @example
 * kebabCase("GetUserID") // "get-user-id"
 */
export const kebabCase = (text: string) => convertCase(text, 'kebab');

/**
 * Converts a string to CONSTANT_CASE.
 *
 * @example
 * constantCase("getUserID2FA") // "GET_USER_ID_2_FA"
 */
export const constantCase = (text: string) => convertCase(text, 'constant');

/**
 * Converts a string to Title Case.
 *
 * @example
 * titleCase("getUserID2FA") // "Get User Id 2 Fa"
 */
export const titleCase = (text: string): string => convertCase(text, 'title');
